import { useEffect, useRef, useState } from "react";
import { useSiteContent } from "../content.js";
import { isDemo } from "../booth/homeDemo.js";

// Site nav: brand on the left; on the right, the one call to action that
// matters most right now — Greg's live booth (/live), red and pulsing while
// he's on the air — and a menu button. Every section link lives in the menu at
// every screen size; the old row of ten links ran too long across the top.
//
// The section links are rendered even while the menu is closed (just hidden),
// so they stay in the document for crawlers, and the regression suite can
// check them without opening the menu.

// Is Greg on the air? Pages that already poll the booth (the /live page) pass
// `boothLive`; everywhere else the nav checks once a minute. The home page's
// demo of his live night (/?booth=demo, src/booth/homeDemo.js) counts as on.
function useBoothLive(override) {
  const [live, setLive] = useState(isDemo);
  useEffect(() => {
    if (override !== undefined || isDemo()) return undefined;
    let stop = false;
    let timer;
    const tick = async () => {
      try {
        const r = await fetch("/api/booth/state", { cache: "no-store" });
        const j = await r.json();
        if (!stop) setLive(!!j.live);
      } catch {
        /* no booth API (local dev) — treat as off the air */
      }
      if (!stop) timer = setTimeout(tick, 60_000);
    };
    tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [override]);
  return override !== undefined ? !!override : live;
}

export default function Nav({ active, boothLive }) {
  const content = useSiteContent();
  const navItems = content.nav.items;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const burgerRef = useRef(null);
  const live = useBoothLive(boothLive);
  const onHome = typeof window === "undefined" || window.location.pathname === "/";
  const onLive = active === "live";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // While the menu is open, Escape closes it and any outside tap dismisses it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Tap or click anywhere outside the menu (and its button) closes it. A
    // document listener rather than a full-screen overlay: the bar's
    // frosted-glass effect would trap a fixed overlay inside the bar.
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || burgerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    // On phones the open menu is a full-screen sheet; freeze the page under it
    // (the CSS only applies this at phone widths).
    document.documentElement.classList.add("nav-menu-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
      document.documentElement.classList.remove("nav-menu-open");
    };
  }, [open]);

  const go = (e, id) => {
    const el = document.getElementById(id);
    // Off the homepage (e.g. /live) the section isn't here — let the link's
    // /#id href load the homepage at that section.
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Reflect the section in the address bar so the link is shareable. replaceState
    // (not location.hash) avoids a native scroll jump that would fight the smooth
    // scroll above, and doesn't pile a history entry onto every click.
    window.history.replaceState(null, "", `#${id}`);
    setOpen(false);
  };

  return (
    <nav className={`nav ${scrolled || open ? "nav-scrolled" : ""}`}>
      <div className="nav-inner">
        <a
          className="nav-brand"
          href="/"
          onClick={(e) => {
            if (!onHome) return;
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
            window.history.replaceState(null, "", "/");
            setOpen(false);
          }}
        >
          <span className="nav-mark">GP</span>
          <span className="nav-name">{content.nav.brand}</span>
        </a>
        <div className="nav-controls">
          <a
            className={`nav-cta ${live ? "is-live" : ""}`}
            href="/live"
            aria-current={onLive ? "page" : undefined}
          >
            <span className="nav-cta-dot" aria-hidden="true" />
            {live ? (
              <>
                <span className="nav-cta-long">Live now: watch Greg</span>
                <span className="nav-cta-short">Live now</span>
              </>
            ) : (
              <>
                <span className="nav-cta-long">Greg's live booth</span>
                <span className="nav-cta-short">Live booth</span>
              </>
            )}
          </a>
          <button
            type="button"
            ref={burgerRef}
            className={`nav-burger ${open ? "is-open" : ""}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="site-menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      <div className="nav-menu" id="site-menu" hidden={!open}>
        <div className="nav-menu-inner" ref={menuRef}>
          <ul
            className="nav-menu-list"
            // Wide screens show two columns, filled top to bottom so the
            // numbering runs down the left column and then the right.
            style={{ "--menu-rows": Math.ceil(navItems.length / 2) }}
          >
            {navItems.map((it) => (
              <li key={it.id}>
                <a
                  href={onHome ? `#${it.id}` : `/#${it.id}`}
                  className={active === it.id ? "is-active" : ""}
                  aria-current={active === it.id ? "location" : undefined}
                  onClick={(e) => go(e, it.id)}
                >
                  <span className="mono">{it.index}</span> {it.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            className={`nav-menu-live ${live ? "is-live" : ""}`}
            href="/live"
            aria-current={onLive ? "page" : undefined}
          >
            <span className="nav-cta-dot" aria-hidden="true" />
            <span className="nav-menu-live-text">
              <b>{live ? "Greg's on the air now" : "Greg's live booth"}</b>
              <small>Live postseason calls, the score, and $1 shoutouts</small>
            </span>
            <span className="nav-menu-live-arrow" aria-hidden="true">
              →
            </span>
          </a>
        </div>
      </div>
    </nav>
  );
}

const navStyles = document.createElement("style");
navStyles.textContent = `
.nav {
  position: fixed; top: 0; left: 0; right: 0;
  z-index: 80;
  padding: 18px 0;
  transition: padding 240ms, background 240ms, backdrop-filter 240ms;
}
.nav-scrolled {
  padding: 12px 0;
  background: rgba(5, 13, 26, 0.78);
  backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid var(--rule);
}
.nav-inner {
  max-width: 1320px; margin: 0 auto; padding: 0 32px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px;
}
.nav-brand {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--serif); font-weight: 600;
  font-size: 1.125rem; letter-spacing: -0.01em;
  color: var(--bone);
  min-width: 0;
}
.nav-name { white-space: nowrap; }
.nav-mark {
  width: 32px; height: 32px; flex: none;
  border: 1px solid var(--royal-blue-bright);
  background: var(--royal-blue);
  color: var(--bone);
  display: grid; place-items: center;
  font-family: var(--mono);
  font-size: 0.6875rem; font-weight: 600;
  letter-spacing: 0;
  border-radius: 50%;
}
.nav-controls { display: flex; align-items: center; gap: 10px; flex: none; }

/* The call to action: gold when Greg's off the air, red + pulsing when he's on. */
.nav-cta {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 11px 18px 11px 15px;
  border-radius: 999px;
  background: var(--gold);
  color: var(--navy-900);
  font-size: 0.875rem; font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  transition: background 200ms, transform 200ms, box-shadow 200ms;
}
.nav-cta:hover { background: var(--gold-bright); transform: translateY(-1px); }
.nav-cta-dot {
  width: 8px; height: 8px; flex: none;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.55;
}
.nav-cta.is-live {
  background: linear-gradient(180deg, #ff5d4f, #c8102e);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 150, 138, 0.6), 0 8px 26px -8px rgba(255, 74, 61, 0.8);
}
.nav-cta.is-live:hover { background: linear-gradient(180deg, #ff6b5e, #d41a38); }
.nav-cta.is-live .nav-cta-dot,
.nav-menu-live.is-live .nav-cta-dot { background: #fff; opacity: 1; animation: nav-tally 1.6s ease-in-out infinite; }
@keyframes nav-tally {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
  50% { box-shadow: 0 0 0 5px rgba(255, 255, 255, 0); }
}
.nav-cta[aria-current="page"] { box-shadow: inset 0 0 0 2px rgba(5, 13, 26, 0.35); }
.nav-cta-short { display: none; }

.nav-burger {
  display: flex;
  width: 44px; height: 44px;
  background: transparent; border: 1px solid var(--rule); border-radius: 999px;
  flex-direction: column; gap: 4px; align-items: center; justify-content: center;
  cursor: pointer;
  transition: border-color 200ms, background 200ms;
}
.nav-burger:hover, .nav-burger.is-open { border-color: var(--gold); background: rgba(189, 155, 96, 0.08); }
.nav-burger span { width: 16px; height: 1.5px; background: var(--bone); display: block; transition: transform 200ms, opacity 200ms; }
.nav-burger.is-open span:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
.nav-burger.is-open span:nth-child(2) { opacity: 0; }
.nav-burger.is-open span:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }
.nav-burger:focus-visible, .nav-cta:focus-visible { outline: 2px solid var(--royal-blue-glow); outline-offset: 2px; }

/* The menu floats below the bar instead of stretching it — otherwise the
   bar's frosted-glass blur would smear everything behind the open menu.
   Wide screens: a two-column panel under the menu button. Phones: a
   full-bleed sheet filling the screen below the bar (media query below). */
.nav-menu {
  position: absolute; top: 100%; left: 0; right: 0;
  max-width: 1320px; margin: 0 auto; padding: 0 32px;
  display: flex; justify-content: flex-end;
  pointer-events: none;
}
.nav-menu[hidden] { display: none; }
.nav-menu-inner {
  pointer-events: auto;
  width: min(560px, 100%);
  margin-top: 8px;
  padding: 12px;
  border-radius: 14px;
  background: var(--navy-800);
  border: 1px solid var(--rule);
  box-shadow: 0 24px 60px -16px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(0, 0, 0, 0.35);
  max-height: calc(100dvh - 92px);
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex; flex-direction: column;
  animation: nav-drop 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
@keyframes nav-drop { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.nav-menu-list {
  list-style: none; margin: 0; padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(var(--menu-rows, 6), auto);
  grid-auto-flow: column;
  column-gap: 8px; row-gap: 2px;
}
.nav-menu-list a {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 12px;
  min-height: 42px;
  border-radius: 8px;
  color: var(--bone-dim);
  font-size: 0.9375rem;
  line-height: 1.2;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  transition: background 140ms, color 140ms;
}
.nav-menu-list a:hover { background: rgba(232, 227, 214, 0.06); color: var(--bone); }
.nav-menu-list a.is-active { background: rgba(189, 155, 96, 0.12); color: var(--bone); }
.nav-menu-list a .mono { flex: none; width: 1.5em; color: var(--gold); font-size: 0.625rem; letter-spacing: 0.08em; }
.nav-menu-list a:focus-visible { outline: 2px solid var(--royal-blue-glow); outline-offset: -2px; }

/* One line for the booth at the foot of the menu. The bar already carries
   the button; this just says what the booth is. */
.nav-menu-live {
  display: flex; align-items: center; gap: 12px;
  margin-top: 10px;
  padding: 14px 12px 6px;
  border-top: 1px solid var(--rule);
  color: var(--bone);
  -webkit-tap-highlight-color: transparent;
}
.nav-menu-live .nav-cta-dot { background: var(--gold); opacity: 1; }
.nav-menu-live.is-live .nav-cta-dot { background: var(--bx-onair, #ff4a3d); }
.nav-menu-live-text { flex: 1; min-width: 0; }
.nav-menu-live b { display: block; font-weight: 600; font-size: 0.9375rem; }
.nav-menu-live.is-live b { color: #ffb3aa; }
.nav-menu-live small { display: block; margin-top: 2px; font-size: 0.75rem; color: var(--bone-dim); opacity: 0.7; }
.nav-menu-live-arrow { flex: none; color: var(--gold); transition: transform 160ms; }
.nav-menu-live:hover .nav-menu-live-arrow { transform: translateX(3px); }

@media (max-width: 640px) {
  .nav-inner { padding: 0 16px; gap: 10px; }
  .nav-cta { padding: 10px 14px 10px 12px; font-size: 0.8125rem; }
  .nav-cta-long { display: none; }
  .nav-cta-short { display: inline; }

  /* Full-bleed sheet under the bar, one section per row, the booth pinned
     to the bottom. The open bar is 12px padding + 44px controls + 12px
     padding + 1px border = 69px tall (opening always applies .nav-scrolled). */
  .nav-menu { padding: 0; }
  .nav-menu-inner {
    width: 100%;
    margin-top: 0;
    padding: 10px 12px 16px;
    border: 0; border-radius: 0;
    box-shadow: none;
    background: linear-gradient(180deg, var(--navy-800), var(--navy-900));
    min-height: calc(100dvh - 69px);
    max-height: calc(100dvh - 69px);
    animation: nav-sheet 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes nav-sheet { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: none; } }
  .nav-menu-list { grid-template-columns: 1fr; grid-template-rows: none; grid-auto-flow: row; row-gap: 0; }
  .nav-menu-list a { min-height: 50px; padding: 10px 12px; font-size: 1.0625rem; border-radius: 10px; }
  .nav-menu-list a .mono { font-size: 0.6875rem; }
  .nav-menu-live { margin-top: auto; padding: 16px 12px 4px; }
  html.nav-menu-open, html.nav-menu-open body { overflow: hidden; }
}
@media (max-width: 360px) {
  .nav-name { font-size: 1rem; }
}
`;
document.head.appendChild(navStyles);
